import { useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import cn from "classnames";
import PropTypes from "prop-types";

import { canvasPreview } from "shared/helpers/crop";

import "react-image-crop/dist/ReactCrop.css";
import styles from "@platformAssets/components/PhotoCrop.module.scss";

const PhotoCrop = ({
  cropSrc,
  imgAngle,
  index,
  setImageLoaded,
  imageRef,
  rotateRef,
  hiddenImg,
  aspect,
  crop,
  setCrop,
  setCroppedImageUrl = null,
  previewCanvasRef,
  pix,
  setPix,
  setCropData,
  className = "",
}) => {
  const [imgSizes, setImgSizes] = useState({
    imgW: 0,
    imgH: 0,
  });

  /**
   * Image loaded handler
   * @param {*} event
   */
  const onImageLoaded = (event) => {
    const img = event.target;
    const imageAspect = img.naturalWidth / img.naturalHeight;
    const percentCropWidth = 100 * (aspect / imageAspect);
    const pixWidth = Math.round(img.naturalWidth * (percentCropWidth / 100));
    const outW = (img.naturalWidth - pixWidth) / 2;
    const { naturalWidth: width, naturalHeight: height } = event.currentTarget;

    const newCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          height: 100,
        },
        aspect,
        width,
        height
      ),
      width,
      height
    );

    const newPix = {
      height: img.naturalHeight,
      width: pixWidth,
      y: 0,
      x: outW,
    };

    setCrop(newCrop);
    setPix(newPix);
    setImgSizes({
      imgW: img.naturalWidth,
      imgH: img.naturalHeight,
    });

    setCropData({
      index,
      crop: newCrop,
      pix: newPix,
      rotate: imgAngle,
    });

    setImageLoaded(true);
  };

  /**
   * Crop change handler
   * @param {object} newPix
   * @param {object} newCrop
   */
  const handleOnCropChange = (newPix, newCrop) => {
    if (
      !isNaN(newCrop.width) &&
      newCrop.width > 0 &&
      (newCrop.width !== crop.width ||
        newCrop.x !== crop.x ||
        newCrop.y !== crop.y)
    ) {
      setCrop({ ...newCrop });
      setPix({
        ...newPix,
        height: Math.round((imgSizes.imgH * newCrop.height) / 100),
        width: Math.round((imgSizes.imgW * newCrop.width) / 100),
        x: newCrop.x ? Math.round((imgSizes.imgW * newCrop.x) / 100) : 0,
        y: newCrop.y ? Math.round((imgSizes.imgH * newCrop.y) / 100) : 0,
      });
    }
  };

  /**
   * Complete crop handler
   * @param {object} newPix
   * @param {object} newCrop
   */
  const onComplete = (newPix, newCrop) => {
    // img data in px
    const newPixData = {
      ...newPix,
      height: Math.round((imgSizes.imgH * newCrop.height) / 100),
      width: Math.round((imgSizes.imgW * newCrop.width) / 100),
      x: newCrop.x ? Math.round((imgSizes.imgW * newCrop.x) / 100) : 0,
      y: newCrop.y ? Math.round((imgSizes.imgH * newCrop.y) / 100) : 0,
    };

    if (newCrop.width > 0) {
      setCropData({
        index,
        crop: newCrop,
        pix: newPixData,
        rotate: imgAngle,
      });
    }
    //create preview
    makeClientCrop();

    if (typeof setCroppedImageUrl === "function") {
      setCroppedImageUrl(true);
    }
  };

  /**
   * Make client crop
   */
  const makeClientCrop = () => {
    if (cropSrc && hiddenImg.current && previewCanvasRef?.current) {
      canvasPreview(
        hiddenImg.current,
        previewCanvasRef.current,
        pix,
        crop,
        imgAngle
      ).then();
    }
  };

  return (
    <>
      <div className={cn(styles.cropSection, className)}>
        <ReactCrop
          crop={crop}
          style={{ transform: `scale(1) rotate(0deg)` }}
          onChange={(pixCrop, percentCrop) =>
            handleOnCropChange(pixCrop, percentCrop)
          }
          onComplete={(pixCrop, percentCrop) =>
            onComplete(pixCrop, percentCrop)
          }
          aspect={aspect}
        >
          <img
            ref={imageRef}
            alt="crop-img"
            src={cropSrc}
            onLoad={onImageLoaded}
          />
        </ReactCrop>
      </div>
      <canvas
        id="crop-canvas-rotate"
        className={styles.cropCanvas}
        ref={rotateRef}
      />
    </>
  );
};

PhotoCrop.propTypes = {
  cropSrc: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  setImageLoaded: PropTypes.func.isRequired,
  imgAngle: PropTypes.number.isRequired,
  aspect: PropTypes.number.isRequired,
  crop: PropTypes.object.isRequired,
  pix: PropTypes.object.isRequired,
  setCrop: PropTypes.func.isRequired,
  setPix: PropTypes.func.isRequired,
  setCroppedImageUrl: PropTypes.func,
  previewCanvasRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  imageRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  hiddenImg: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  rotateRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  setCropData: PropTypes.func.isRequired,
};

export default PhotoCrop;
